import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useMemo } from 'react';
import { useGameStore } from '../store/gameStore';
import { ArrowLeft, Trophy, Flame, Zap, Star, Gift, Lock, CheckCircle2, Share2 } from 'lucide-react';
import { playCoin, playComplete, playClick } from '../utils/sounds';
import ShareModal, { type ShareData } from '../components/ShareModal';

// Achievement definitions
export interface AchievementDef {
  id: string;
  name: string;
  icon: string;
  description: string;
  category: 'training' | 'streak' | 'performance' | 'milestone' | 'special';
  tier: 'bronze' | 'silver' | 'gold';
  target: number;
  reward: number; // energy reward
}

export const ACHIEVEMENTS: AchievementDef[] = [
  // Training achievements
  { id: 'train_1', name: '初次体验', icon: '🎮', description: '完成第1次训练', category: 'training', tier: 'bronze', target: 1, reward: 5 },
  { id: 'train_5', name: '训练新手', icon: '🎯', description: '完成5次训练', category: 'training', tier: 'bronze', target: 5, reward: 10 },
  { id: 'train_20', name: '勤练不辍', icon: '💪', description: '完成20次训练', category: 'training', tier: 'silver', target: 20, reward: 25 },
  { id: 'train_50', name: '训练达人', icon: '🏅', description: '完成50次训练', category: 'training', tier: 'silver', target: 50, reward: 50 },
  { id: 'train_100', name: '训练大师', icon: '👑', description: '完成100次训练', category: 'training', tier: 'gold', target: 100, reward: 100 },
  { id: 'train_200', name: '传奇训练师', icon: '🌟', description: '完成200次训练', category: 'training', tier: 'gold', target: 200, reward: 200 },

  // Streak achievements
  { id: 'streak_3', name: '三日之约', icon: '🔥', description: '连续训练3天', category: 'streak', tier: 'bronze', target: 3, reward: 15 },
  { id: 'streak_7', name: '一周达人', icon: '📅', description: '连续训练7天', category: 'streak', tier: 'silver', target: 7, reward: 35 },
  { id: 'streak_14', name: '半月坚守', icon: '🌙', description: '连续训练14天', category: 'streak', tier: 'silver', target: 14, reward: 70 },
  { id: 'streak_30', name: '月度冠军', icon: '🏆', description: '连续训练30天', category: 'streak', tier: 'gold', target: 30, reward: 150 },
  { id: 'streak_60', name: '铁人意志', icon: '💎', description: '连续训练60天', category: 'streak', tier: 'gold', target: 60, reward: 300 },

  // Performance achievements
  { id: 'acc_80', name: '初具慧眼', icon: '👁️', description: '单次训练准确率≥80%', category: 'performance', tier: 'bronze', target: 80, reward: 10 },
  { id: 'acc_90', name: '火眼金睛', icon: '🦅', description: '单次训练准确率≥90%', category: 'performance', tier: 'silver', target: 90, reward: 20 },
  { id: 'acc_95', name: '鹰眼猎手', icon: '🎖️', description: '单次训练准确率≥95%', category: 'performance', tier: 'silver', target: 95, reward: 40 },
  { id: 'acc_100', name: '完美视界', icon: '✨', description: '单次训练准确率100%', category: 'performance', tier: 'gold', target: 100, reward: 80 },
  { id: 'react_800', name: '快速反应', icon: '⚡', description: '平均反应时间≤800ms', category: 'performance', tier: 'bronze', target: 800, reward: 15 },
  { id: 'react_500', name: '闪电速度', icon: '💨', description: '平均反应时间≤500ms', category: 'performance', tier: 'silver', target: 500, reward: 30 },
  { id: 'react_300', name: '光速反应', icon: '🚀', description: '平均反应时间≤300ms', category: 'performance', tier: 'gold', target: 300, reward: 60 },

  // Milestone achievements
  { id: 'energy_50', name: '能量储备', icon: '🔋', description: '累计获得50能量', category: 'milestone', tier: 'bronze', target: 50, reward: 10 },
  { id: 'energy_200', name: '能量充沛', icon: '⚡', description: '累计获得200能量', category: 'milestone', tier: 'silver', target: 200, reward: 25 },
  { id: 'energy_500', name: '能量大师', icon: '💫', description: '累计获得500能量', category: 'milestone', tier: 'silver', target: 500, reward: 50 },
  { id: 'energy_1000', name: '能量帝王', icon: '👑', description: '累计获得1000能量', category: 'milestone', tier: 'gold', target: 1000, reward: 100 },
  { id: 'games_3cat', name: '多面手', icon: '🎨', description: '在3个分类中完成训练', category: 'milestone', tier: 'bronze', target: 3, reward: 20 },
  { id: 'games_6cat', name: '全能视者', icon: '🌈', description: '在全部6个分类中完成训练', category: 'milestone', tier: 'gold', target: 6, reward: 80 },

  // Special achievements
  { id: 'first_perfect', name: '首次满分', icon: '🎉', description: '第一次获得100%准确率', category: 'special', tier: 'silver', target: 1, reward: 30 },
  { id: 'night_owl', name: '夜间护眼', icon: '🦉', description: '使用护眼模式累计1小时', category: 'special', tier: 'bronze', target: 3600, reward: 15 },
  { id: 'eye_guard', name: '护眼卫士', icon: '🛡️', description: '使用护眼模式累计5小时', category: 'special', tier: 'silver', target: 18000, reward: 40 },
  { id: 'questionnaire', name: '健康自查', icon: '📋', description: '完成一次视力检测问卷', category: 'special', tier: 'bronze', target: 1, reward: 10 },
  { id: 'vision_record', name: '视力档案', icon: '📝', description: '记录一次验光数据', category: 'special', tier: 'bronze', target: 1, reward: 10 },
];

const CATEGORY_CONFIG = {
  training: { label: '训练成就', icon: '🏋️', color: 'from-blue-500 to-cyan-500' },
  streak: { label: '连续打卡', icon: '🔥', color: 'from-orange-500 to-red-500' },
  performance: { label: '表现卓越', icon: '⚡', color: 'from-yellow-500 to-amber-500' },
  milestone: { label: '里程碑', icon: '🌟', color: 'from-purple-500 to-violet-500' },
  special: { label: '特殊成就', icon: '🎁', color: 'from-pink-500 to-rose-500' },
};

const TIER_COLORS = {
  bronze: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-300 dark:border-amber-700', ring: '#CD7F32' },
  silver: { bg: 'bg-gray-100 dark:bg-gray-700/50', text: 'text-gray-600 dark:text-gray-300', border: 'border-gray-300 dark:border-gray-600', ring: '#C0C0C0' },
  gold: { bg: 'bg-yellow-50 dark:bg-yellow-900/20', text: 'text-yellow-700 dark:text-yellow-300', border: 'border-yellow-400 dark:border-yellow-600', ring: '#FFD700' },
};

export default function AchievementPage() {
  const navigate = useNavigate();
  const { 
    levelResults, consecutiveDays, totalEnergy, badges,
    eyeProtectionTotalSeconds, visionQuestionnaireResults, visionRecords,
    achievements, claimAchievementReward, playerName
  } = useGameStore();
  
  const [activeTab, setActiveTab] = useState<'training' | 'streak' | 'performance' | 'milestone' | 'special'>('training');
  const [showCelebration, setShowCelebration] = useState<string | null>(null);
  const [shareData, setShareData] = useState<ShareData | null>(null);

  // Calculate progress for each achievement
  const achievementProgress = useMemo(() => {
    const progress: Record<string, { current: number; unlocked: boolean }> = {};
    
    // Get unique categories from results
    const categoriesPlayed = new Set(levelResults.filter(r => r.levelId).map(r => {
      // Extract category from levelId (format: categoryId-gameIndex)
      const parts = r.levelId.split('-');
      return parts[0];
    }));

    for (const ach of ACHIEVEMENTS) {
      let current = 0;
      let unlocked = false;

      switch (ach.category) {
        case 'training':
          current = levelResults.length;
          unlocked = current >= ach.target;
          break;
        case 'streak':
          current = consecutiveDays;
          unlocked = current >= ach.target;
          break;
        case 'performance':
          if (ach.id.startsWith('acc_')) {
            const bestAccuracy = levelResults.length > 0 
              ? Math.max(...levelResults.map(r => r.accuracy)) 
              : 0;
            current = Math.round(bestAccuracy);
            unlocked = bestAccuracy >= ach.target;
          } else if (ach.id.startsWith('react_')) {
            const bestReaction = levelResults.some(r => r.reactionTime > 0)
              ? Math.min(...levelResults.filter(r => r.reactionTime > 0).map(r => r.reactionTime))
              : 9999;
            current = Math.round(bestReaction);
            // For reaction time, lower is better, so unlocked when current <= target
            unlocked = bestReaction <= ach.target && levelResults.length > 0;
          }
          break;
        case 'milestone':
          if (ach.id.startsWith('energy_')) {
            current = totalEnergy;
            unlocked = current >= ach.target;
          } else if (ach.id === 'games_3cat' || ach.id === 'games_6cat') {
            current = categoriesPlayed.size;
            unlocked = current >= ach.target;
          }
          break;
        case 'special':
          if (ach.id === 'first_perfect') {
            const hasPerfect = levelResults.some(r => r.accuracy >= 100);
            current = hasPerfect ? 1 : 0;
            unlocked = hasPerfect;
          } else if (ach.id === 'night_owl' || ach.id === 'eye_guard') {
            current = eyeProtectionTotalSeconds;
            unlocked = current >= ach.target;
          } else if (ach.id === 'questionnaire') {
            current = visionQuestionnaireResults.length;
            unlocked = current >= ach.target;
          } else if (ach.id === 'vision_record') {
            current = visionRecords.length;
            unlocked = current >= ach.target;
          }
          break;
      }

      progress[ach.id] = { current, unlocked };
    }

    return progress;
  }, [levelResults, consecutiveDays, totalEnergy, eyeProtectionTotalSeconds, visionQuestionnaireResults, visionRecords]);

  const handleClaim = (achId: string) => {
    const ach = ACHIEVEMENTS.find(a => a.id === achId);
    if (!ach) return;
    const claimed = achievements.find(a => a.id === achId)?.claimed;
    if (claimed) return;
    
    claimAchievementReward(achId, ach.reward);
    playCoin();
    setShowCelebration(achId);
    setTimeout(() => {
      playComplete();
      setTimeout(() => setShowCelebration(null), 2000);
    }, 300);
  };

  const handleShareAchievement = (ach: AchievementDef) => {
    playClick();
    setShareData({
      type: 'achievement',
      title: '成就达成！',
      playerName: playerName,
      achievement: {
        icon: ach.icon,
        name: ach.name,
        description: ach.description,
        tier: ach.tier,
      },
      stats: [
        { label: '已解锁', value: `${unlockedCount}/${totalAchievements}` },
        { label: '连续天数', value: `${consecutiveDays}天` },
        { label: '累计能量', value: `${totalEnergy}⚡` },
      ],
      summary: `我在视界探险中解锁了「${ach.name}」成就！`,
    });
  };

  const filteredAchievements = ACHIEVEMENTS.filter(a => a.category === activeTab);
  
  const totalAchievements = ACHIEVEMENTS.length;
  const unlockedCount = Object.values(achievementProgress).filter(p => p.unlocked).length;
  const claimedCount = achievements.filter(a => a.claimed).length;
  const totalRewardsEarned = achievements.filter(a => a.claimed).reduce((sum, a) => {
    const def = ACHIEVEMENTS.find(d => d.id === a.id);
    return sum + (def?.reward || 0);
  }, 0);

  const containerAnim = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
  const cardAnim = { hidden: { y: 16, opacity: 0 }, show: { y: 0, opacity: 1, transition: { duration: 0.35 } } };

  return (
    <div className="min-h-screen w-full bg-[var(--bg)]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between max-w-2xl mx-auto px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-300">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-bold text-gray-800 dark:text-gray-100">🏆 成就殿堂</h1>
          <div className="w-9" />
        </div>
      </header>

      <motion.div
        variants={containerAnim}
        initial="hidden"
        animate="show"
        className="max-w-2xl mx-auto px-4 py-6 pb-28 space-y-5"
      >
        {/* Stats Overview */}
        <motion.div variants={cardAnim} className="bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500 rounded-2xl p-5 text-white shadow-lg shadow-amber-200/40 dark:shadow-none">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-amber-100 text-xs">成就进度</p>
              <p className="text-2xl font-bold mt-0.5">{unlockedCount} / {totalAchievements}</p>
            </div>
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
              <Trophy className="w-7 h-7" />
            </div>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden mb-3">
            <motion.div
              className="h-full bg-white/80 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(unlockedCount / totalAchievements) * 100}%` }}
              transition={{ delay: 0.3, duration: 0.8 }}
            />
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1">
              <Gift className="w-3.5 h-3.5" /> 已领取 {claimedCount} 个奖励
            </span>
            <span className="flex items-center gap-1">
              <Zap className="w-3.5 h-3.5" /> 累计获得 {totalRewardsEarned} 能量
            </span>
          </div>
        </motion.div>

        {/* Category Tabs */}
        <motion.div variants={cardAnim} className="grid grid-cols-2 gap-2">
          {(Object.keys(CATEGORY_CONFIG) as Array<keyof typeof CATEGORY_CONFIG>).map(cat => {
            const cfg = CATEGORY_CONFIG[cat];
            const catAchs = ACHIEVEMENTS.filter(a => a.category === cat);
            const catUnlocked = catAchs.filter(a => achievementProgress[a.id]?.unlocked).length;
            return (
              <button
                key={cat}
                onClick={() => setActiveTab(cat)}
                className={`w-full rounded-xl px-3 py-2.5 text-xs font-medium transition-all ${
                  activeTab === cat
                    ? 'bg-gradient-to-r ' + cfg.color + ' text-white shadow-md'
                    : 'border border-gray-200 bg-white text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
                }`}
              >
                <div className="flex items-center justify-center gap-1">
                  <span>{cfg.icon}</span>
                  <span>{cfg.label}</span>
                </div>
                <div className="mt-0.5 text-[10px] opacity-80">{catUnlocked}/{catAchs.length}</div>
              </button>
            );
          })}
        </motion.div>

        {/* Achievement Cards */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
            className="space-y-3"
          >
            {filteredAchievements.map(ach => {
              const prog = achievementProgress[ach.id];
              const isClaimed = achievements.find(a => a.id === ach.id)?.claimed || false;
              const tierStyle = TIER_COLORS[ach.tier];
              
              // Calculate progress percentage
              let progressPercent = 0;
              if (ach.category === 'performance' && ach.id.startsWith('react_')) {
                // For reaction time, show inverse progress (lower is better)
                progressPercent = prog.unlocked ? 100 : Math.min(100, Math.max(0, ((9999 - prog.current) / (9999 - ach.target)) * 100));
              } else if (ach.category === 'special' && (ach.id === 'night_owl' || ach.id === 'eye_guard')) {
                progressPercent = Math.min(100, (prog.current / ach.target) * 100);
              } else {
                progressPercent = Math.min(100, (prog.current / ach.target) * 100);
              }

              return (
                <motion.div
                  key={ach.id}
                  layout
                  className={`bg-white dark:bg-gray-800 rounded-2xl p-4 border-2 transition-all ${
                    prog.unlocked
                      ? isClaimed
                        ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/30 dark:bg-emerald-900/10'
                        : 'border-amber-300 dark:border-amber-600 shadow-md shadow-amber-100 dark:shadow-none'
                      : 'border-gray-100 dark:border-gray-700 opacity-80'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${tierStyle.bg} border ${tierStyle.border}`}>
                      {prog.unlocked ? ach.icon : '🔒'}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold text-sm ${prog.unlocked ? 'text-gray-800 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
                          {ach.name}
                        </h3>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${tierStyle.bg} ${tierStyle.text}`}>
                          {ach.tier === 'bronze' ? '铜' : ach.tier === 'silver' ? '银' : '金'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{ach.description}</p>
                      
                      {/* Progress bar */}
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                          <motion.div
                            className={`h-full rounded-full ${
                              prog.unlocked
                                ? 'bg-gradient-to-r from-emerald-400 to-teal-400'
                                : 'bg-gradient-to-r from-gray-300 to-gray-400 dark:from-gray-500 dark:to-gray-600'
                            }`}
                            initial={{ width: 0 }}
                            animate={{ width: `${progressPercent}%` }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                          />
                        </div>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0">
                          {ach.category === 'performance' && ach.id.startsWith('react_')
                            ? `${prog.current > 9000 ? '---' : prog.current + 'ms'} / ≤${ach.target}ms`
                            : ach.category === 'special' && (ach.id === 'night_owl' || ach.id === 'eye_guard')
                              ? `${Math.floor(prog.current / 60)}分 / ${Math.floor(ach.target / 60)}分`
                              : `${prog.current} / ${ach.target}`
                          }
                        </span>
                      </div>
                    </div>

                    {/* Claim Button */}
                    <div className="flex-shrink-0">
                      {prog.unlocked && !isClaimed ? (
                        <motion.button
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleClaim(ach.id)}
                          className="px-3 py-1.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-semibold rounded-lg shadow-md shadow-amber-200 dark:shadow-none"
                        >
                          +{ach.reward}⚡
                        </motion.button>
                      ) : isClaimed ? (
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => handleShareAchievement(ach)}
                            className="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-500 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors"
                          >
                            <Share2 className="w-3.5 h-3.5" />
                          </button>
                          <span className="text-emerald-500 dark:text-emerald-400">
                            <CheckCircle2 className="w-5 h-5" />
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">
                          <Lock className="w-4 h-4" />
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>

        {/* Reward Legend */}
        <motion.div variants={cardAnim} className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-500" /> 奖励说明
          </h3>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3">
              <div className="w-8 h-8 mx-auto rounded-full flex items-center justify-center mb-1" style={{ background: 'linear-gradient(135deg, #CD7F32, #8B4513)' }}>
                <span className="text-white text-xs font-bold">铜</span>
              </div>
              <p className="text-[10px] text-gray-600 dark:text-gray-400">5-20能量</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
              <div className="w-8 h-8 mx-auto rounded-full flex items-center justify-center mb-1" style={{ background: 'linear-gradient(135deg, #C0C0C0, #808080)' }}>
                <span className="text-white text-xs font-bold">银</span>
              </div>
              <p className="text-[10px] text-gray-600 dark:text-gray-400">20-70能量</p>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-3">
              <div className="w-8 h-8 mx-auto rounded-full flex items-center justify-center mb-1" style={{ background: 'linear-gradient(135deg, #FFD700, #B8860B)' }}>
                <span className="text-white text-xs font-bold">金</span>
              </div>
              <p className="text-[10px] text-gray-600 dark:text-gray-400">60-300能量</p>
            </div>
          </div>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center mt-3">
            完成成就后点击领取按钮获得能量奖励，能量可在商店中兑换道具
          </p>
        </motion.div>
      </motion.div>

      {/* Celebration Overlay */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm pointer-events-none"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-2xl text-center"
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                transition={{ duration: 0.6 }}
                className="text-5xl mb-3"
              >
                {ACHIEVEMENTS.find(a => a.id === showCelebration)?.icon}
              </motion.div>
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">
                🎉 成就达成！
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {ACHIEVEMENTS.find(a => a.id === showCelebration)?.name}
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 font-medium">
                +{ACHIEVEMENTS.find(a => a.id === showCelebration)?.reward} 能量
              </p>
              {/* Confetti particles */}
              {Array.from({ length: 12 }).map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    background: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96E6A1', '#DDA0DD'][i % 6],
                    left: '50%',
                    top: '50%',
                  }}
                  initial={{ x: 0, y: 0, opacity: 1 }}
                  animate={{
                    x: Math.cos((i * 30 * Math.PI) / 180) * 80,
                    y: Math.sin((i * 30 * Math.PI) / 180) * 80,
                    opacity: 0,
                    scale: [1, 1.5, 0],
                  }}
                  transition={{ duration: 1, delay: 0.1 }}
                />
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Modal */}
      <AnimatePresence>
        {shareData && (
          <ShareModal data={shareData} onClose={() => setShareData(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}
